import { useEffect, useMemo, useRef } from 'react';
import { getQuoteVersion, mutateQuoteComposition } from '../quote.api';
import { editorFromQuote } from '../quoteEditorModel';
import { localChangesDuringWrite } from '../quoteRevision';
import useCompositionEditor from './useCompositionEditor';

export default function useOperationalComposition({ editor, setEditor }) {
  const latest = useRef(editor); latest.current = editor;
  const quoteId = editor?.id, versionId = editor?.quoteVersionId;
  const enabled = editor?.contentSchemaVersion === 2;
  const pending = useRef(false);
  const connector = useMemo(() => enabled && quoteId && versionId ? {
    // QuotePage has already loaded this authenticated server projection.
    // A second initial read can arrive during the user's first mutation and
    // replace the editor, falsely reporting new local input during that write.
    initialModel: latest.current.composition,
    beforeReload: () => !pending.current || window.confirm('Abandonner les saisies non enregistrées et recharger la version serveur ?'),
    load: async () => {
      const submitted = latest.current;
      const quote = await getQuoteVersion(quoteId, versionId);
      if (String(latest.current?.quoteVersionId) !== String(versionId)) throw new Error('La version sélectionnée a changé.');
      if (latest.current !== submitted) throw new Error('Vos saisies ont été conservées. Rechargez explicitement pour remplacer le contenu local.');
      setEditor(editorFromQuote(quote)); return quote.composition;
    },
    execute: async ({ expectedRevision, command }) => {
      const submitted = latest.current;
      if (String(latest.current?.quoteVersionId) !== String(versionId)) throw new Error('La version sélectionnée a changé.');
      if (latest.current.serverCalculationStatus === 'stale' || latest.current.serverWriteConflict)
        throw new Error('Enregistrez les informations modifiées avant une commande de composition.');
      const result = await mutateQuoteComposition(versionId, command, expectedRevision);
      const quote = await getQuoteVersion(quoteId, versionId);
      if (String(latest.current?.quoteVersionId) !== String(versionId)) throw new Error('La version sélectionnée a changé après la sauvegarde.');
      if (latest.current !== submitted) throw localChangesDuringWrite({ quoteId, quoteVersionId: versionId });
      setEditor(editorFromQuote(quote)); return { ...quote.composition, idMapping: result.idMapping };
    },
  } : null, [enabled, quoteId, versionId, setEditor]);
  const composition = useCompositionEditor(connector);
  pending.current = composition.hasPendingChanges;
  const { accept } = composition;
  const projection = editor?.composition;
  useEffect(() => { if (enabled && projection) accept(projection); }, [enabled, projection, accept]);
  return enabled && composition.model ? { ...composition, operational: true } : null;
}
